<template>
  <div class="top-clients">
    <!-- Skeleton -->
    <div v-if="loading" class="tc-skeleton">
      <div v-for="n in 5" :key="n" class="tc-skeleton-row">
        <div class="skeleton-avatar"></div>
        <div class="skeleton-line w60"></div>
        <div class="skeleton-line w30"></div>
      </div>
    </div>

    <!-- List -->
    <div v-else-if="clients.length" class="tc-list">
      <div
        v-for="(client, idx) in clients"
        :key="client.id || idx"
        class="tc-item"
        style="cursor: pointer;"
        @click="$emit('open-client', client)"
      >
        <div class="tc-rank">{{ idx + 1 }}</div>
        <div class="tc-avatar" :style="{ background: avatarColor(idx) }">
          {{ initials(client.name) }}
        </div>
        <div class="tc-info">
          <span class="tc-name">{{ client.name }}</span>
          <span class="tc-meta">
            {{ client.invoice_count }} fact. | Conv:
            {{ client.conversion_rate }}%
          </span>
        </div>
        <span class="tc-amount">${{ formatMoney(client.total_invoiced) }}</span>
      </div>
    </div>

    <!-- Empty -->
    <div v-else class="tc-empty">
      <i class="el-icon-user"></i>
      <span>Sin datos de clientes</span>
    </div>
  </div>
</template>

<script>
import dashboardService from "../services/dashboardService";

const AVATAR_COLORS = ["#31355c", "#534ab7", "#3fdbbf", "#ba7517", "#1d9e75"];

export default {
  name: "TopClients",
  props: {
    period: { type: String, default: "month" }
  },
  emits: ["open-client"],
  data() {
    return {
      loading: true,
      cache: {},
      clients: []
    };
  },
  watch: {
    period: {
      immediate: true,
      handler(val) {
        this.fetchClients(val);
      }
    }
  },
  methods: {
    async fetchClients(period) {
      if (this.cache[period]) {
        this.clients = this.cache[period];
        return;
      }

      this.loading = true;
      try {
        const res = await dashboardService.getTopClients({
          params: { period, limit: 5 }
        });
        if (res.data && res.data.success) {
          this.clients = res.data.data.clients || res.data.data || [];
          this.cache[period] = this.clients;
        }
      } catch (e) {
        // Mantener valores actuales
      } finally {
        this.loading = false;
      }
    },
    avatarColor(idx) {
      return AVATAR_COLORS[idx % AVATAR_COLORS.length];
    },
    initials(name) {
      if (!name) return "?";
      const parts = name.trim().split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    formatMoney(val) {
      return new Intl.NumberFormat("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(val || 0);
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$accent: #3fdbbf;
$border: #e4eaf6;
$bg: #f7f8f9;

.top-clients {
  background: #fff;
  border: 1px solid $border;
  border-radius: 10px;
  padding: 18px;
}
.tc-list {
  display: flex;
  flex-direction: column;
}
.tc-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid $border;
  &:last-child {
    border-bottom: none;
  }
}
.tc-rank {
  font-size: 11px;
  font-weight: 700;
  color: #c0c4cc;
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}
.tc-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}
.tc-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.tc-name {
  font-size: 13px;
  font-weight: 500;
  color: $primary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tc-meta {
  font-size: 12px;
  color: #909399;
}
.tc-amount {
  font-size: 13px;
  font-weight: 500;
  color: $primary;
  flex-shrink: 0;
}

.tc-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  color: #c0c4cc;
  gap: 8px;
  i {
    font-size: 28px;
  }
  span {
    font-size: 12px;
  }
}

// Skeleton
.tc-skeleton {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.tc-skeleton-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.skeleton-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  flex-shrink: 0;
}
.skeleton-line {
  height: 12px;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  &.w30 {
    width: 30%;
  }
  &.w60 {
    width: 60%;
  }
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
